import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Activity, Heart, TrendingUp, TrendingDown, Minus, AlertCircle, Plus,
  MessageSquare, Building2, AlertTriangle, ShieldAlert, DatabaseZap, CheckCircle2,
  ClipboardList, ArrowRight, Wifi, WifiOff, Zap, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { api } from '../utils/api';
import AddPatientModal from './AddPatientModal';
import AddPatientModalEnhanced from './AddPatientModalEnhanced';
import InsuranceModal from './InsuranceModal';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [priorityPatients, setPriorityPatients] = useState([]);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [dataSeeded, setDataSeeded] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [selectedPatientForInsurance, setSelectedPatientForInsurance] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, priorityRes] = await Promise.all([
        api.getAnalyticsSummary(),
        api.getPriorityPatients(),
      ]);
      setAnalytics(analyticsRes.data);
      setPriorityPatients(priorityRes.data.slice(0, 5));
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.clear();
        navigate('/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      setLoading(true);
      await api.seedData();
      toast.success('Sample data loaded successfully!');
      setDataSeeded(true);
      await loadDashboardData();
    } catch (error) {
      toast.error('Failed to load sample data');
    } finally {
      setLoading(false);
    }
  };

  const getRiskLabel = (score) => {
    if (score === 1) return 'Critical';
    if (score === 2) return 'High Risk';
    if (score === 3) return 'Monitor';
    if (score === 4) return 'Low Risk';
    return 'Stable';
  };

  const getRiskBadgeStyle = (score) => {
    if (score === 1) return 'bg-red-600 text-white';
    if (score === 2) return 'bg-orange-500 text-white';
    if (score === 3) return 'bg-yellow-500 text-white';
    if (score === 4) return 'bg-blue-500 text-white';
    return 'bg-green-500 text-white';
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getPrimaryReasonFlagged = (patient) => {
    if (patient.digital_twin_score < 55) return 'Critical DTS decline';
    if (patient.digital_twin_score < 65) return 'Low Digital Twin Score';
    if (patient.mood_score < 4) return 'Mood score critically low';
    if (patient.mood_score < 5) return 'Low mood trend';
    if (patient.risk_score === 1) return 'Critical risk tier';
    if (patient.risk_score === 2) return 'Elevated risk indicators';
    return 'Monitoring threshold reached';
  };

  const handleQuickAction = (e, patientId, action) => {
    e.stopPropagation();
    if (action === 'message') {
      navigate('/messages');
    } else if (action === 'checkin') {
      toast.success('Check-in request sent!');
    }
  };

  const handleAddPayor = (e, patient) => {
    e.stopPropagation();
    setSelectedPatientForInsurance(patient);
    setShowInsuranceModal(true);
  };

  // Mock data for Open Alerts and Blockers
  const openAlerts = [
    { id: 1, member: 'Marcus Johnson', type: 'Health Status', desc: 'DTS dropped below 60 — review vitals', severity: 'critical', time: '2h ago' },
    { id: 2, member: 'Elena Rodriguez', type: 'Mood Alert', desc: 'Mood score below 4 for 3 consecutive days', severity: 'high', time: '5h ago' },
    { id: 3, member: 'James Wilson', type: 'Adherence', desc: 'Missed 4 consecutive check-ins', severity: 'high', time: '1d ago' },
    { id: 4, member: 'Sarah Chen', type: 'Biometric', desc: 'SpO2 reading below threshold', severity: 'medium', time: '1d ago' },
  ];

  const blockers = [
    { id: 1, member: 'Marcus Johnson', type: 'Device', desc: 'Wearable device not syncing — last sync 76 days ago', severity: 'critical', action: 'Reconnect Device' },
    { id: 2, member: 'James Wilson', type: 'Enrollment', desc: 'Emergency services signup incomplete', severity: 'high', action: 'Complete Setup' },
    { id: 3, member: 'Ana Martinez', type: 'Technical', desc: 'App login credentials expired', severity: 'medium', action: 'Reset Access' },
  ];

  if (loading && !analytics) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-64"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="skeleton h-24"></div>)}
        </div>
      </div>
    );
  }

  if (!analytics || analytics.active_patients === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-purple-100 mb-6">
            <Users className="w-10 h-10 text-purple-700" />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>No Members Yet</h2>
          <p className="text-gray-600 mb-6">
            Get started by loading sample member data or adding your first member to begin monitoring health journeys.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleSeedData} className="rounded-full px-8 bg-purple-700 hover:bg-purple-800" data-testid="seed-data-btn">
              Load Sample Data
            </Button>
            <Button onClick={() => setShowAddPatient(true)} variant="outline" className="rounded-full px-8" data-testid="add-first-member-btn">
              Add First Member
            </Button>
          </div>
        </div>
        <AddPatientModal open={showAddPatient} onClose={() => setShowAddPatient(false)} onSuccess={loadDashboardData} />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="dashboard-title">Dashboard Overview</h1>
          <p className="text-gray-600 mt-1">Monitor member health and identify priority cases</p>
        </div>
        <Button onClick={() => setShowAddPatient(true)} className="rounded-full bg-purple-700 hover:bg-purple-800" data-testid="add-member-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* KPI Row 1: Core Counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/patients')} data-testid="kpi-active-members">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Members</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.active_patients}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="w-4 h-4 text-purple-700" />
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-2 hover:underline">View all members &rarr;</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/patients?filter=priority')} data-testid="kpi-flagged">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Members Flagged</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.flagged_members}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Risk tier 1-3</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-needs-attention">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Needs Attention</p>
                <p className="text-2xl font-bold mt-1 text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.needs_attention}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                <ShieldAlert className="w-4 h-4 text-red-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Critical + High risk</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-missing-data">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Missing Data</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.missing_data}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                <DatabaseZap className="w-4 h-4 text-gray-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">3+ days no sync/check-in</p>
          </CardContent>
        </Card>
      </div>

      {/* KPI Row 2: Operational */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-open-alerts">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Open Alerts</p>
                <p className="text-2xl font-bold mt-1 text-orange-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.open_alerts}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Health status concerns</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-blockers">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Blockers</p>
                <p className="text-2xl font-bold mt-1 text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.blockers}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                <WifiOff className="w-4 h-4 text-red-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Flow/technical issues</p>
          </CardContent>
        </Card>

        <Card data-testid="kpi-adherence">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Avg Adherence</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif', color: analytics.avg_adherence >= 85 ? '#16a34a' : '#ea580c' }}>
                  {analytics.avg_adherence}%
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">{analytics.avg_adherence >= 85 ? 'Above' : 'Below'} 85% target</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/careteam/inbox')} data-testid="kpi-actions-today">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Actions Today</p>
                <p className="text-2xl font-bold mt-1 text-purple-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{analytics.actions_today}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                <ClipboardList className="w-4 h-4 text-purple-700" />
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-2 hover:underline">Go to Action Center &rarr;</p>
          </CardContent>
        </Card>
      </div>

      {/* Open Alerts & Blockers - Two Column */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Open Alerts */}
        <Card data-testid="open-alerts-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Open Alerts</CardTitle>
                <Badge variant="secondary" className="text-xs">{openAlerts.length}</Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-xs text-purple-700" onClick={() => navigate('/careteam/inbox')}>
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Health status changes requiring clinical review</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {openAlerts.map((alert) => (
                <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors cursor-pointer" data-testid={`alert-${alert.id}`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                    alert.severity === 'critical' ? 'bg-red-500' : alert.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">{alert.member}</p>
                      <span className="text-xs text-gray-400 shrink-0 ml-2">{alert.time}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{alert.desc}</p>
                    <span className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                      alert.severity === 'critical' ? 'bg-red-100 text-red-700' : alert.severity === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{alert.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Blockers */}
        <Card data-testid="blockers-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <WifiOff className="w-5 h-5 text-red-600" />
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Blockers</CardTitle>
                <Badge variant="secondary" className="text-xs">{blockers.length}</Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-xs text-purple-700" onClick={() => navigate('/careteam/inbox')}>
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Environmental or technical issues blocking member progress</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {blockers.map((blocker) => (
                <div key={blocker.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors cursor-pointer" data-testid={`blocker-${blocker.id}`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                    blocker.severity === 'critical' ? 'bg-red-500' : blocker.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 truncate">{blocker.member}</p>
                      <Button variant="outline" size="sm" className="text-[10px] h-6 px-2 shrink-0 ml-2">{blocker.action}</Button>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{blocker.desc}</p>
                    <span className={`inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                      blocker.type === 'Device' ? 'bg-gray-200 text-gray-700' : blocker.type === 'Enrollment' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>{blocker.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Members Needing Attention */}
      <Card data-testid="priority-members-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <AlertCircle className="w-5 h-5 text-red-600" />
                Members Needing Attention
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                {analytics.priority_patients} member{analytics.priority_patients !== 1 ? 's' : ''} flagged for priority review
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/patients?filter=priority')} className="text-sm" data-testid="view-all-priority-btn">
              View All
            </Button>
          </div>
          <div className="mt-2 px-3 py-1.5 bg-purple-50 rounded-lg text-xs text-gray-600">
            <strong>Sorted by:</strong> Risk Tier (Critical first) &mdash; Showing Name, Risk Tier, Primary Reason Flagged
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {priorityPatients.map((patient, index) => (
              <div
                key={patient.id}
                className="group relative flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-purple-200 transition-all cursor-pointer"
                onClick={() => navigate(`/patients/${patient.id}`)}
                data-testid={`priority-member-${patient.id}`}
              >
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                  index === 0 ? 'bg-red-100 text-red-700 border-2 border-red-300' :
                  index === 1 ? 'bg-orange-100 text-orange-700 border-2 border-orange-300' :
                  'bg-gray-100 text-gray-600 border-2 border-gray-200'
                }`}>
                  {index + 1}
                </div>

                {/* Member Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">{patient.first_name} {patient.last_name}</h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${getRiskBadgeStyle(patient.risk_score)}`}>
                      {getRiskLabel(patient.risk_score)}
                    </span>
                    {patient.insurance_provider && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        <Building2 className="w-3 h-3 mr-1" />
                        {patient.insurance_provider.length > 18 ? patient.insurance_provider.substring(0, 18) + '...' : patient.insurance_provider}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    <span className="font-medium">Flagged:</span> {getPrimaryReasonFlagged(patient)}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                    <span>DTS: <span className={`font-semibold ${patient.digital_twin_score < 65 ? 'text-red-600' : 'text-gray-700'}`}>{patient.digital_twin_score}</span></span>
                    <span className="text-gray-300">|</span>
                    <span>Mood: <span className={`font-semibold ${patient.mood_score < 5 ? 'text-red-600' : 'text-gray-700'}`}>{patient.mood_score}/10</span></span>
                    <span className="text-gray-300">|</span>
                    <span>Sync: <span className="font-medium">{getTimeAgo(patient.last_sync)}</span></span>
                  </div>
                </div>

                {/* Quick Actions - always visible, high z-index */}
                <div className="flex items-center gap-2 shrink-0 relative z-10">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-8 px-3"
                    onClick={(e) => handleQuickAction(e, patient.id, 'message')}
                    data-testid={`quick-message-${patient.id}`}
                  >
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Message
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-8 px-3"
                    onClick={(e) => handleQuickAction(e, patient.id, 'checkin')}
                    data-testid={`quick-checkin-${patient.id}`}
                  >
                    <Activity className="w-3 h-3 mr-1" />
                    Check-In
                  </Button>
                </div>
              </div>
            ))}

            {priorityPatients.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No priority members at this time</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AddPatientModalEnhanced
        open={showAddPatient}
        onClose={() => setShowAddPatient(false)}
        onSuccess={loadDashboardData}
      />

      <InsuranceModal
        open={showInsuranceModal}
        onClose={() => {
          setShowInsuranceModal(false);
          setSelectedPatientForInsurance(null);
        }}
        patient={selectedPatientForInsurance}
        onSuccess={loadDashboardData}
      />
    </div>
  );
};

export default Dashboard;
